import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useOMS } from '@/apps/hooks/oms/useOMS';
import { getConfig } from '@/config';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import {
  OMSRequestError,
  type AuditHistoryEntryInput,
  type CloneOrderRequest,
  type InboundRequest,
  type LineItem,
  type OpsComment,
  type OrderAddress,
  type OrderSummary,
  type OrderUpdateRequest,
  ShippingProviderType,
  type SwitchShipmentProviderRequest,
} from '@/infrastructure/OMSClient';
import type { OrderMutationResult } from '@/models/OrderMutationResult';
import { GigWorkerClient } from '@/infrastructure/GigWorkerClient';
import { CloneOrderModal } from '@/pages/oms/orders/[id]/CloneOrderModal';
import { GigOrderDetailAction } from '@/pages/oms/orders/[id]/GigOrderDetailAction';
import { OrderDetail } from '@/pages/oms/orders/[id]/OrderDetail';
import { SwitchShippingProviderModal } from '@/pages/oms/orders/[id]/SwitchShippingProviderModal';
import GigOrderDetailModal from '@/pages/oms/gig-workers/GigOrderDetailModal';
import { useAuth } from '@/providers/AuthProvider';
import { Button, Row, Col, OverlayTrigger, Tooltip, Modal, Form } from 'react-bootstrap';
import { ArrowLeft, Copy, History, Trash, ExternalLink } from 'lucide-react';
import { toast } from 'react-hot-toast';

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { fromOrders?: string } | null;
  const backToOrdersUrl = locationState?.fromOrders || '/oms/orders';
  const [stockStatusBySku, setStockStatusBySku] = useState<Record<string, number>>({});
  const oms = useOMS();
  const gigWorkerClient = useMemo(
    () => new GigWorkerClient(getAdminApiUrl(AdminApiDomain.GIGWORK), getConfig().apiKey),
    [],
  );
  const fetchingStockStatusRef = useRef(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Order detail is fetched via React Query so concurrent/duplicate requests for the same id are
  // deduped and cached. This replaces the manual useEffect + setTimeout ref-lock, which could not
  // dedupe across component remounts (e.g. the auth-gated route mounting the page twice).
  const {
    data: order = null,
    isLoading: loading,
    isLoadingError,
    refetch,
  } = useQuery({
    queryKey: ['order', id],
    queryFn: () => oms.getOrderDetail(id!),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
  });
  // Only surface the full-screen error on an initial-load failure with no cached order. A failed
  // background refetch (e.g. after a mutation's refetch()) keeps the cached `order`, so we must not
  // swap a valid order view for the generic error UI in that case.
  const error = !order && isLoadingError ? 'Failed to fetch order details' : null;

  const {
    data: gigOrderReference,
    isError: isGigOrderLookupError,
    refetch: refetchGigOrderReference,
  } = useQuery({
    queryKey: ['gig-order-reference-by-oms', order?.orderId],
    queryFn: () => gigWorkerClient.getOrderReferenceByOmsOrderRef(order!.orderId),
    enabled: Boolean(order?.orderId),
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Adapters so existing call sites keep working unchanged:
  // - setOrder(x): write the order straight into the query cache (used after mutations that already
  //   return the updated order, avoiding an extra refetch).
  // - fetchOrder(): re-run the query; returns a promise so `await fetchOrder()` still works.
  const setOrder = (value: OrderSummary | null) => queryClient.setQueryData(['order', id], value);
  const fetchOrder = () => refetch();

  // Add state for cancel order modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelValid, setCancelValid] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Add state for clone order modal
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showSwitchProviderModal, setShowSwitchProviderModal] = useState(false);
  const [showGigOrderModal, setShowGigOrderModal] = useState(false);

  useEffect(() => {
    setShowGigOrderModal(false);
  }, [id]);

  // Add state for creating order
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  // Create audit entry helper function
  const createAuditEntry = (): AuditHistoryEntryInput => ({
    operator: user?.name || 'Unknown',
    source: 'OPERATOR',
  });

  const handleUpdateLineItem = async (lineItem: LineItem) => {
    if (!order) {
      return;
    }

    try {
      // Check if this is a new item (no itemId) or an existing one
      const isNewItem = !lineItem.itemId;

      const result = await oms.updateOrder(order.orderId, {
        lineItems: [lineItem],
        auditEntry: createAuditEntry(),
      });

      if (result) {
        setOrder(result);
        toast.success(isNewItem ? 'Line item added successfully' : 'Line item updated successfully');
      }
    } catch (err) {
      console.error('Error updating line item:', err);
      toast.error(`Failed to update line item: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleUpdateComments = async (opsComments: OpsComment[]) => {
    if (!order) {
      return;
    }

    try {
      const request: OrderUpdateRequest = {
        opsComments: opsComments,
        auditEntry: createAuditEntry(),
      };

      const result = await oms.updateOrder(order.orderId, request);
      if (result) {
        setOrder(result);
      }
    } catch (err) {
      console.error('Error updating comments:', err);
      toast.error(`Failed to update comments: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleUpdatePic = async (pic: string) => {
    if (!order) {
      return;
    }

    try {
      const request: OrderUpdateRequest = {
        pic: pic,
        auditEntry: createAuditEntry(),
      };

      const result = await oms.updateOrder(order.orderId, request);
      if (result) {
        setOrder(result);
        toast.success('PIC updated successfully');
      }
    } catch (err) {
      console.error('Error updating PIC:', err);
      toast.error('Failed to update PIC');
    }
  };

  const handleUpdateBillingAddress = async (billingAddress: OrderAddress) => {
    if (!order) {
      return;
    }

    try {
      const request: OrderUpdateRequest = {
        billingAddress: billingAddress,
        auditEntry: createAuditEntry(),
      };

      const result = await oms.updateOrder(order.orderId, request);
      if (result) {
        setOrder(result);
        toast.success('Billing address updated successfully');
      }
    } catch (err) {
      console.error('Error updating billing address:', err);
      toast.error('Failed to update billing address');
    }
  };

  const handleUpdateShippingAddress = async (shippingAddress: OrderAddress) => {
    if (!order) {
      return;
    }

    try {
      const request: OrderUpdateRequest = {
        shippingAddress: shippingAddress,
        auditEntry: createAuditEntry(),
      };

      const result = await oms.updateOrder(order.orderId, request);
      if (result) {
        setOrder(result);
        toast.success('Shipping address updated successfully');
      }
    } catch (err) {
      console.error('Error updating shipping address:', err);
      toast.error('Failed to update shipping address');
    }
  };

  const handleRequestInbound = async (inboundRequest: Omit<InboundRequest, 'auditEntry'>) => {
    try {
      console.debug('inboundRequest', inboundRequest);

      // Add audit entry to the inbound request
      const requestWithAudit: InboundRequest = {
        ...inboundRequest,
        auditEntry: createAuditEntry(),
      };

      const response = await oms.requestInbound(id!, requestWithAudit);
      if (response) {
        await fetchOrder(); // Using the locked fetchOrder function
        toast.success(`Inbound request created successfully`);
      }
      console.debug('response', response);
    } catch (error) {
      console.error('Failed to request inbound:', error);
      toast.error(`Failed to request inbound: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleCancelInbound = async (inboundId: string) => {
    try {
      const response = await oms.cancelInbound(id!, inboundId, createAuditEntry());
      if (response) {
        await fetchOrder();
        toast.success('Successfully cancelled inbound request');
      }
      console.debug('response', response);
    } catch (error) {
      console.error('Error cancelling inbound:', error);
      toast.error('Failed to cancel inbound request: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim() || !user) {
      setCancelValid(false);
      return;
    }

    setIsCancelling(true);

    try {
      const response = await oms.cancelOrder(
        id!,
        {
          reason: 'Cancellation reason: ' + cancelReason.trim(),
          operator: user.name,
        },
        createAuditEntry(),
      );

      if (response) {
        await fetchOrder();
        toast.success('Order cancelled successfully');
        setShowCancelModal(false);
        setCancelReason('');
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast.error('Failed to cancel order: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsCancelling(false);
    }
  };

  const openCancelModal = () => {
    if (!user) {
      toast.error('You must be logged in to cancel an order');
      return;
    }
    setShowCancelModal(true);
    setCancelValid(false);
    setCancelReason('');
  };

  const openCloneModal = () => {
    if (!user) {
      toast.error('You must be logged in to clone an order');
      return;
    }
    setShowCloneModal(true);
  };

  const formatMutationError = (error: unknown): string => {
    if (error instanceof OMSRequestError && error.operationId) {
      return `${error.message} (operation ${error.operationId}${error.state ? `, state ${error.state}` : ''})`;
    }
    return error instanceof Error ? error.message : 'Unknown error';
  };

  const handleCloneOrder = async (cloneOrderRequest: CloneOrderRequest): Promise<OrderMutationResult> => {
    try {
      const response = await oms.cloneOrder(cloneOrderRequest);

      if (response) {
        // Show success message with clickable order number
        const toastId = toast.success(
          <div>
            Order cloned successfully!
            <br />
            <button
              onClick={() => {
                toast.dismiss(toastId);
                navigate(`/oms/orders/${response.orderId}`);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#0066cc',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: 0,
                fontSize: 'inherit',
              }}
            >
              Click here to view Order #{response.orderId}
            </button>
          </div>,
          { duration: 6000 },
        );
        return { success: true };
      }
      return { success: false, error: 'The backend did not return the cloned order.' };
    } catch (error) {
      return { success: false, error: formatMutationError(error) };
    }
  };

  const handleSwitchProvider = async (request: SwitchShipmentProviderRequest): Promise<OrderMutationResult> => {
    if (!order) {
      return { success: false, error: 'Order not loaded.' };
    }

    try {
      const response = await oms.switchShipmentProvider(order.orderId, request);
      if (response.state !== 'COMPLETED') {
        return {
          success: false,
          error: `Provider switch stopped in ${response.state} (operation ${response.operationId}). Reconcile before retrying.`,
        };
      }

      const verifiedOrder = await oms.getOrderDetail(order.orderId);
      if (
        !verifiedOrder ||
        verifiedOrder.shippingData?.provider !== request.targetProvider ||
        !verifiedOrder.shippingData.providerOrderId
      ) {
        return {
          success: false,
          error: `Operation ${response.operationId} completed but the target shipment projection was not visible. Do not retry; reconcile first.`,
        };
      }

      setOrder(verifiedOrder);
      toast.success(`Shipping provider switched to ${request.targetProvider}`);
      return { success: true };
    } catch (error) {
      // Cancellation may have cleared the active shipment. Reload the durable operation so
      // recovery remains available even when no current provider can be displayed.
      try {
        const latest = await oms.getOrderDetail(order.orderId);
        if (latest) {
          setOrder(latest);
        }
      } catch {
        // The modal retains its original request identity when even the reload is unavailable.
      }
      return {
        success: false,
        error: formatMutationError(error),
        preflightRejected:
          error instanceof OMSRequestError &&
          ['FEDEX_ADDRESS_INVALID', 'FEDEX_PREFLIGHT_VALIDATION_FAILED'].includes(error.code ?? '') &&
          !error.operationId,
      };
    }
  };

  const handleGetStockStatus = async (skus: string[]) => {
    if (!order || !order.lineItems || fetchingStockStatusRef.current) {
      return;
    }

    try {
      // get stock status for each sku
      // Remove duplicate SKUs before making the API call
      const uniqueSkus = Array.from(new Set(skus));
      fetchingStockStatusRef.current = true;
      const stockStatus = await oms.getStockStatus(uniqueSkus.join(','));
      if (stockStatus) {
        const stockStatusBySku = stockStatus.items.reduce(
          (acc, item) => {
            acc[item.sku] = item.quantity;
            return acc;
          },
          {} as Record<string, number>,
        );
        setStockStatusBySku(stockStatusBySku);
      }
    } catch (error) {
      console.error('Error getting stock status:', error);
    } finally {
      setTimeout(() => {
        fetchingStockStatusRef.current = false;
      }, 1000); // release the lock after 1 second to prevent concurrent fetching
    }
  };

  const handleCreateAutoRoutedShipment = async () => {
    if (!order || !order.orderId || !user || isCreatingOrder) {
      return;
    }

    setIsCreatingOrder(true);
    const loadingToastId = toast.loading('Creating shipment...');

    try {
      const response: OrderSummary | null = await oms.createAutoRoutedShipment(
        order.orderId,
        'Manual auto-routed shipment creation requested from OMS Admin',
      );
      if (response) {
        setOrder(response);
        toast.success(
          `Shipment created successfully with ${response.shippingData?.provider || 'the auto-routed provider'}`,
          { id: loadingToastId },
        );
      } else {
        toast.error('Failed to create shipment', { id: loadingToastId });
      }
    } catch (error) {
      console.error('Error creating auto-routed shipment:', error);
      toast.error(`Error creating shipment: ${formatMutationError(error)}`, {
        id: loadingToastId,
      });
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleCreateShipment = async (provider: ShippingProviderType) => {
    if (!order || !order.orderId || !user || isCreatingOrder) {
      return;
    }

    setIsCreatingOrder(true);
    toast.success(`Creating ${provider} shipment...`);

    try {
      const response: OrderSummary | null = await oms.createShipment(order.orderId, {
        reason: `Manual ${provider} shipment creation requested from OMS Admin`,
        deliveryProvider: provider,
      });
      if (response) {
        setOrder(response);
        toast.success(`${provider} shipment created successfully`);
      } else {
        toast.error(`Failed to create ${provider} shipment`);
      }
    } catch (error) {
      console.error(`Error creating ${provider} shipment:`, error);
      toast.error(`Error creating ${provider} shipment: ` + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleResetDeleoOrder = async (): Promise<OrderMutationResult> => {
    if (!order || !order.orderId || !user) {
      return { success: false, error: 'Order or authenticated operator is unavailable.' };
    }

    const loadingToastId = toast.loading('Resetting Deleo order...');
    try {
      const response = await oms.resetDeleoOrder(order.orderId, 'Manual Deleo shipment reset requested from OMS Admin');
      if (response) {
        await fetchOrder();
        toast.dismiss(loadingToastId);
        toast.success('Deleo order reset successfully');
        return { success: true };
      } else {
        toast.dismiss(loadingToastId);
        toast.error('Failed to reset Deleo order');
        return { success: false, error: 'The backend did not return the reset order.' };
      }
    } catch (error) {
      console.error('Error resetting Deleo order:', error);
      toast.dismiss(loadingToastId);
      toast.error('Error resetting Deleo order: ' + (error instanceof Error ? error.message : 'Unknown error'));
      return { success: false, error: formatMutationError(error) };
    }
  };

  const handleResetParxlOrder = async (): Promise<OrderMutationResult> => {
    if (!order || !order.orderId || !user) {
      return { success: false, error: 'Order or authenticated operator is unavailable.' };
    }

    const loadingToastId = toast.loading('Resetting Parxl order...');
    try {
      const response = await oms.resetParxlOrder(order.orderId, 'Manual Parxl shipment reset requested from OMS Admin');
      if (response) {
        await fetchOrder();
        toast.dismiss(loadingToastId);
        toast.success('Parxl order reset successfully');
        return { success: true };
      } else {
        toast.dismiss(loadingToastId);
        toast.error('Failed to reset Parxl order');
        return { success: false, error: 'The backend did not return the reset order.' };
      }
    } catch (error) {
      console.error('Error resetting Parxl order:', error);
      toast.dismiss(loadingToastId);
      toast.error('Error resetting Parxl order: ' + (error instanceof Error ? error.message : 'Unknown error'));
      return { success: false, error: formatMutationError(error) };
    }
  };

  const handleUpdateTrackingNumber = async (newProviderOrderId: string): Promise<OrderMutationResult> => {
    if (!order) {
      return { success: false, error: 'Order not loaded' };
    }

    try {
      // Step 1: replace the YunExpress waybill number (providerOrderId).
      await oms.updateOrder(order.orderId, {
        shippingData: { providerOrderId: newProviderOrderId },
        auditEntry: createAuditEntry(),
      });
    } catch (err) {
      console.error('Error updating tracking number:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to update tracking number: ${message}`);
      return { success: false, error: message };
    }

    let refresh: { success: boolean; error?: string } | null = null;
    let refreshError: unknown = null;

    try {
      // Step 2: refresh tracking against the new number.
      refresh = await oms.refreshShippingStatus(order.orderId);
    } catch (err) {
      console.error('Error refreshing shipping status:', err);
      refreshError = err;
    }

    try {
      // Step 1 already persisted the new number, so re-fetch regardless of
      // the refresh outcome to show the current state.
      const updated = await oms.getOrderDetail(order.orderId);
      if (updated) {
        setOrder(updated);
      }
    } catch (err) {
      console.error('Error fetching updated order details:', err);
    }

    if (refreshError) {
      const message = refreshError instanceof Error ? refreshError.message : 'Unknown error';
      toast.error(`Tracking number changed, but tracking refresh failed: ${message}`);
      return { success: false, error: message };
    }

    // A 200 response can still carry success=false (number not resolvable).
    if (refresh && !refresh.success) {
      const message = refresh.error ?? 'Unknown error';
      toast.error(`Tracking number changed, but tracking refresh failed: ${message}`);
      return { success: false, error: message };
    }

    toast.success('Tracking number updated successfully');
    return { success: true };
  };

  const handleResetYunOrder = async (): Promise<OrderMutationResult> => {
    if (!order || !order.orderId || !user) {
      return { success: false, error: 'Order or authenticated operator is unavailable.' };
    }

    const loadingToastId = toast.loading('Resetting YUN order...');
    try {
      const response = await oms.resetYunOrder(order.orderId, 'Manual YUN shipment reset requested from OMS Admin');
      if (response) {
        await fetchOrder();
        toast.dismiss(loadingToastId);
        toast.success('YUN order reset successfully');
        return { success: true };
      } else {
        toast.dismiss(loadingToastId);
        toast.error('Failed to reset YUN order');
        return { success: false, error: 'The backend did not return the reset order.' };
      }
    } catch (error) {
      console.error('Error resetting YUN order:', error);
      toast.dismiss(loadingToastId);
      toast.error('Error resetting YUN order: ' + (error instanceof Error ? error.message : 'Unknown error'));
      return { success: false, error: formatMutationError(error) };
    }
  };

  const handleCreateOrder = async () => {
    if (!id || !user) {
      toast.error('Order ID is required');
      return;
    }

    setIsCreatingOrder(true);

    try {
      const response = await oms.createOrder(id);
      if (response) {
        toast.success('Order created successfully');
        await fetchOrder();
      } else {
        toast.error('Failed to create order');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsCreatingOrder(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!order) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '400px' }}>
        <h3 className="mb-4">Order not found</h3>
        <p className="text-muted mb-4">Order ID: {id}</p>
        <Button variant="primary" onClick={handleCreateOrder} disabled={isCreatingOrder}>
          {isCreatingOrder ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Creating Order...
            </>
          ) : (
            'Create Order'
          )}
        </Button>
      </div>
    );
  }

  const shipmentSwitch = order.shipmentSwitch?.state !== 'COMPLETED' ? order.shipmentSwitch : undefined;
  const sourceShipment =
    shipmentSwitch?.sourceProvider && shipmentSwitch.sourceProviderOrderId
      ? { provider: shipmentSwitch.sourceProvider, providerOrderId: shipmentSwitch.sourceProviderOrderId }
      : order.shippingData?.provider &&
          order.shippingData.provider !== ShippingProviderType.FEDEX &&
          order.shippingData.providerOrderId
        ? { provider: order.shippingData.provider, providerOrderId: order.shippingData.providerOrderId }
        : null;

  return (
    <div className="space-y-6">
      <Row>
        <Col xs={6} className="text-start">
          <Button
            variant="outline-primary"
            onClick={() => navigate(backToOrdersUrl)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Orders
          </Button>
        </Col>

        <Col xs={6} className="text-end">
          <GigOrderDetailAction
            reference={gigOrderReference}
            lookupFailed={isGigOrderLookupError}
            onOpen={() => setShowGigOrderModal(true)}
            onRetry={() => void refetchGigOrderReference()}
          />
          <span className="me-4">
            <Button variant="outline-secondary" onClick={() => navigate(`/oms/orders/${id}/audit`)}>
              <History className="h-4 w-4 me-2" />
              Audit History
            </Button>
          </span>
          <span className="me-4">
            <Button
              variant="outline-info"
              onClick={() => {
                const config = getConfig();
                const wpUrl = config.wpAdminUrl.replace(/\/$/, '');
                window.open(`${wpUrl}/admin.php?page=wc-orders&action=edit&id=${id}`, '_blank');
              }}
            >
              <ExternalLink className="h-4 w-4 me-2" />
              View in WP
            </Button>
          </span>
          <span className="me-4">
            <Button variant="outline-primary" onClick={openCloneModal}>
              <Copy className="h-4 w-4 me-2" />
              Clone Order
            </Button>
          </span>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {['cancelled', 'shipped', 'completed'].includes(order.status)
                  ? `This order is already ${order.status}`
                  : order.status !== 'processing'
                    ? "Only orders in 'processing' status can be cancelled"
                    : order.deleoData?.deleoStatus?.name &&
                        !['REQUEST_ORDER', 'REQUEST_OUTBOUND'].includes(order.deleoData.deleoStatus.name)
                      ? "Only orders with Deleo status 'REQUEST_ORDER' or 'REQUEST_OUTBOUND' can be cancelled"
                      : ''}
              </Tooltip>
            }
          >
            <span>
              <Button
                variant="outline-danger"
                disabled={
                  ['cancelled', 'shipped', 'completed'].includes(order.status) ||
                  order.status !== 'processing' ||
                  (order.deleoData?.deleoStatus?.name !== undefined &&
                    !['REQUEST_ORDER', 'REQUEST_OUTBOUND'].includes(order.deleoData.deleoStatus.name))
                }
                onClick={openCancelModal}
              >
                <Trash className="h-4 w-4 me-2" />
                Cancel Order
              </Button>
            </span>
          </OverlayTrigger>
        </Col>
      </Row>

      {/* Cancel Order Modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please provide a reason for canceling this order:</p>
          <Form.Group>
            <Form.Label>
              Cancellation Reason <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={cancelReason}
              onChange={(e) => {
                setCancelReason(e.target.value);
                setCancelValid(e.target.value.trim().length > 0);
              }}
              required
              placeholder="Enter detailed reason for cancellation"
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Operator</Form.Label>
            <Form.Control type="text" value={user?.name || ''} disabled />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
            Close
          </Button>
          <Button variant="danger" onClick={handleCancelOrder} disabled={!cancelValid || isCancelling}>
            {isCancelling ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Cancelling...
              </>
            ) : (
              'Confirm Cancellation'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <CloneOrderModal
        show={showCloneModal}
        order={order}
        operatorName={user?.name || user?.email || 'Authenticated dashboard operator'}
        onHide={() => setShowCloneModal(false)}
        onSubmit={handleCloneOrder}
      />

      {sourceShipment && (
        <SwitchShippingProviderModal
          show={showSwitchProviderModal}
          orderId={order.orderId}
          sourceProvider={sourceShipment.provider}
          sourceProviderOrderId={sourceShipment.providerOrderId}
          {...(shipmentSwitch ? { operation: shipmentSwitch } : {})}
          onHide={() => setShowSwitchProviderModal(false)}
          onSubmit={handleSwitchProvider}
        />
      )}

      <GigOrderDetailModal
        client={gigWorkerClient}
        reference={showGigOrderModal ? (gigOrderReference ?? null) : null}
        onHide={() => setShowGigOrderModal(false)}
      />

      <OrderDetail
        order={order}
        onUpdateLineItem={handleUpdateLineItem}
        onRequestInbound={handleRequestInbound}
        onUpdateComments={handleUpdateComments}
        onUpdatePic={handleUpdatePic}
        onUpdateBillingAddress={handleUpdateBillingAddress}
        onUpdateShippingAddress={handleUpdateShippingAddress}
        onGetStockStatus={handleGetStockStatus}
        stockStatusBySku={stockStatusBySku}
        onCreateOrder={handleCreateAutoRoutedShipment}
        isCreatingOrder={isCreatingOrder}
        onCreateShipment={handleCreateShipment}
        onResetDeleoOrder={handleResetDeleoOrder}
        onResetParxlOrder={handleResetParxlOrder}
        onResetYunOrder={handleResetYunOrder}
        {...(sourceShipment ? { onSwitchShippingProvider: () => setShowSwitchProviderModal(true) } : {})}
        onUpdateTrackingNumber={handleUpdateTrackingNumber}
        onCancelInbound={handleCancelInbound}
        onRefreshOrder={fetchOrder}
      />
    </div>
  );
}
